import { TransactionSourceType } from '@pflegenavi/shared/api';
import { useTransactionApi } from '@pflegenavi/frontend/api-nursing-home';
import { useCallback } from 'react';
import { isDefined } from '@pflegenavi/shared/utils';
import * as Sentry from '@sentry/react';
import imageCompression from 'browser-image-compression';
import { renderPdfToImages } from './renderPdfToImages';

// Concurrent receipt fetches. S3 is HTTP/2, so 8 in flight is faster than 4
// without exhausting browser connections.
const MAX_CONCURRENT_FETCHES = 8;

/**
 * Execute async operations with limited concurrency to prevent connection exhaustion.
 */
async function throttledMap<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  concurrency: number
): Promise<R[]> {
  const results: R[] = [];
  let index = 0;

  async function worker(): Promise<void> {
    while (index < items.length) {
      const currentIndex = index++;
      results[currentIndex] = await fn(items[currentIndex]);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

export interface PreparedReceiptImage {
  dataUrl: string;
  img: HTMLImageElement;
}

export interface FileInfo {
  uri: string;
  // Getter: starts the body download only on first read. Don't spread a
  // FileInfo — that forces the download and drops discardBody.
  data: Promise<Blob>;
  // Cancels the still-unread body so a discarded file's bytes aren't downloaded.
  discardBody?: () => void;
  fileType: string | null;
  isPdf: boolean;
  isImage: boolean;
  // Compression started during fetch (see warmUpForPdf); drawReceiptImages awaits it.
  preparedForPdf?: Promise<PreparedReceiptImage>;
}

/** Reads a Blob as a base64 data URL. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Decodes an image source (here a data URL) into an HTMLImageElement. */
function decodeImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function resizeAndCompressImage(file: FileInfo): Promise<File> {
  const imgData = await file.data;

  return await imageCompression(
    new File(
      [imgData],
      'file.' + (file.fileType ? file.fileType.replace(/image\//, '') : ''),
      { type: file.fileType ?? undefined }
    ),
    {
      // JPEG output so it embeds as a raw DCT stream (no canvas re-encode);
      // 1280px / q0.7 keeps receipts legible while cutting compression cost.
      maxSizeMB: 2,
      maxWidthOrHeight: 1280,
      initialQuality: 0.7,
      fileType: 'image/jpeg',
      useWebWorker: true,
    }
  );
}

/** Resize/compress a receipt image to a JPEG data URL and decode it for sizing. */
export async function prepareReceiptImage(
  file: FileInfo
): Promise<PreparedReceiptImage> {
  const compressed = await resizeAndCompressImage(file);
  const dataUrl = await blobToDataUrl(compressed);
  const img = await decodeImage(dataUrl);
  return { dataUrl, img };
}

/** Start compression during fetch so it overlaps the remaining downloads. */
function warmUpForPdf(fileInfo: FileInfo): void {
  fileInfo.preparedForPdf = prepareReceiptImage(fileInfo);
  // Swallow here only to avoid an unhandled rejection when unconsumed;
  // drawReceiptImages still awaits preparedForPdf and sees the real error.
  void fileInfo.preparedForPdf.catch(() => undefined);
}

/**
 * Minimal transaction shape required by getImages.
 * Exported so callers can build type-safe adapters for custom data sources.
 */
export interface TransactionForReceiptImages {
  id: string;
  amountInCent: number;
  additionalFeeAmountInCent: number;
  includedFeeAmountInCent: number;
  additionalFeesDivertedToPflegenavi: boolean;
  receiptImageIds?: string[];
  receiptDate?: Date;
  receiptType?: string;
  serviceProvider?: string;
  date: Date;
  sourceType: TransactionSourceType;
  cancelled: boolean;
  sequenceNumber: number;
  sequenceID?: string;
}

export interface ReceiptAttachmentTransactionData {
  id: string;
  amount: number;
  additionalFeeAmount: number;
  includedFeeAmount: number;
  additionalFeesDivertedToPflegenavi: boolean;
  receiptDate?: Date;
  receiptType?: string;
  serviceProvider?: string;
  cancelled: boolean; // Is this transaction itself cancelled?
  cancellation: boolean; // Is this transaction the cancellation of (=reversing) another transaction?
  cancellationDate?: Date;
  sequenceID?: string;
}

export interface ReceiptAttachment {
  image: FileInfo;
  receiptNumber: number;
  count: number;
  sequenceNumber: number;
  receiptImagesLength: number;
  transactionData: ReceiptAttachmentTransactionData;
}

export async function fetchFile(fileUrl: string): Promise<FileInfo> {
  const response = await fetch(fileUrl);
  const extractedFileType = response.headers.get('content-type');

  const isPdf = extractedFileType === 'application/pdf';
  const isImage = extractedFileType?.startsWith('image/') ?? false;

  if (!isPdf && !isImage) {
    // eslint-disable-next-line no-console
    console.warn(
      `Fetched file with content-type ${extractedFileType} is not supported: ${fileUrl}`
    );
    Sentry.captureMessage(
      `Fetched file with content-type ${extractedFileType} is not supported: ${fileUrl}`,
      'warning'
    );
  }

  // Download the body lazily (and cancel it if discarded) so a pass that only
  // needs the content-type doesn't pull the bytes of files it throws away.
  let bodyPromise: Promise<Blob> | null = null;

  return {
    uri: fileUrl,
    get data() {
      if (!bodyPromise) {
        bodyPromise = response.blob();
      }
      return bodyPromise;
    },
    discardBody() {
      if (!bodyPromise) {
        response.body?.cancel().catch(() => undefined);
      }
    },
    fileType: extractedFileType,
    isPdf: isPdf,
    isImage: isImage,
  };
}

/**
 * Fetches all receipt files (images and PDFs) for the given transactions,
 * rendering PDF pages as JPEG images so they can be embedded in the PDF report.
 *
 * @example
 * const allImages = await getImagesIncludingPdfPages(transactions, getUris);
 */
async function getImagesIncludingPdfPages(
  transactions: TransactionForReceiptImages[],
  getUris: (
    imageIds: string[]
  ) => Promise<Record<string, string | undefined> | undefined>
): Promise<ReceiptAttachment[]> {
  // Retrieve all receipt ids
  const receiptIds = transactions
    .flatMap((transaction) => transaction.receiptImageIds)
    .filter(isDefined);

  // Get map between receipt id and receipt url
  const receiptImagesObject = await getUris(receiptIds);

  // Fetch all files and classify them (with throttling to prevent connection exhaustion)
  const entries = Object.entries(receiptImagesObject ?? {});
  const asEntries = await throttledMap(
    entries,
    async ([key, value]): Promise<[string, FileInfo[]]> => {
      if (!value) {
        return [key, []];
      }
      const fileInfo = await fetchFile(value);
      if (fileInfo.isImage) {
        warmUpForPdf(fileInfo);
        return [key, [fileInfo]];
      }
      if (fileInfo.isPdf) {
        // Render PDF pages to images
        try {
          const pdfBlob = await fileInfo.data;
          const pageBlobs = await renderPdfToImages(pdfBlob);
          const pageFileInfos: FileInfo[] = pageBlobs.map((blob) => {
            const pageFileInfo: FileInfo = {
              uri: fileInfo.uri,
              data: Promise.resolve(blob),
              fileType: 'image/jpeg',
              isPdf: false,
              isImage: true,
            };
            warmUpForPdf(pageFileInfo);
            return pageFileInfo;
          });
          return [key, pageFileInfos];
        } catch (err) {
          Sentry.captureException(err);
          // eslint-disable-next-line no-console
          console.error('Failed to render PDF to images:', err);
          return [key, []];
        }
      }
      // Unsupported type — don't download its body.
      fileInfo.discardBody?.();
      return [key, []];
    },
    MAX_CONCURRENT_FETCHES
  );

  const receiptIdToFileInfos = Object.fromEntries(asEntries);

  // Build ReceiptAttachment[] with correct counts per transaction
  return transactions.flatMap((transaction, index) => {
    const receiptNumber = index + 1;

    const allFileInfos = (transaction.receiptImageIds ?? []).flatMap(
      (receiptImageId) => receiptIdToFileInfos[receiptImageId] ?? []
    );

    const receiptImagesLength = allFileInfos.length;
    return allFileInfos.map((fileInfo, count) => {
      const isCancellation =
        transaction.sourceType === TransactionSourceType.CancelledReceipt;
      return {
        image: fileInfo,
        receiptNumber,
        count: count + 1,
        sequenceNumber: transaction.sequenceNumber,
        receiptImagesLength,
        transactionData: {
          id: transaction.id,
          amount: transaction.amountInCent,
          additionalFeeAmount: transaction.additionalFeeAmountInCent,
          includedFeeAmount: transaction.includedFeeAmountInCent,
          additionalFeesDivertedToPflegenavi:
            transaction.additionalFeesDivertedToPflegenavi,
          receiptDate: transaction.receiptDate,
          serviceProvider: transaction.serviceProvider,
          receiptType: transaction.receiptType,
          cancelled: transaction.cancelled,
          cancellation: isCancellation,
          cancellationDate: isCancellation ? transaction.date : undefined,
          sequenceID: transaction.sequenceID,
        },
      };
    });
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useReceiptImagesForPdf = () => {
  const api = useTransactionApi();

  const getImagesIncludingPdfsFromReceiptIds = useCallback(
    async (transactions: TransactionForReceiptImages[]) => {
      return await getImagesIncludingPdfPages(
        transactions,
        api.getReceiptImageUrls.bind(api)
      );
    },
    [api]
  );

  return {
    getImagesIncludingPdfsFromReceiptIds,
  };
};
